import React, { useEffect, useRef, useState } from "react";
import "./contact.css";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import { submitContact } from "../../../api/Contact/contact.service";
import { ChevronDown, Phone, Mail, MapPin } from "lucide-react";

import { getCountries, Country } from "../../../api/country/countryapi";
interface FormData {
  name: string;
  email: string;
  contactNumber: string;
  message: string;
}

export const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    contactNumber: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "name") {
      const valid = /^[A-Za-z\s]{0,50}$/.test(value);

      setErrors((prev) => ({
        ...prev,
        name: valid
          ? ""
          : "Sorry, your input failed. Please enter a valid name.",
      }));
    }

    if (name === "contactNumber") {
      const valid = /^[0-9]{0,15}$/.test(value);

      setErrors((prev) => ({
        ...prev,
        contactNumber: valid
          ? ""
          : "Sorry, your input failed. Please enter a valid contact number.",
      }));
    }
  };
  const validateName = (name: string) => {
    const trimmed = name.trim();

    if (!trimmed) {
      return "Name is required.";
    }

    if (trimmed.length < 2) {
      return "Name must be at least 2 characters.";
    }

    if (trimmed.length > 50) {
      return "Name cannot exceed 50 characters.";
    }

    // Allows letters, spaces, apostrophes, and hyphens
    const regex = /^[A-Za-z]+([ '-][A-Za-z]+)*$/;

    if (!regex.test(trimmed)) {
      return "Sorry, your input failed. Please enter a valid name.";
    }

    return "";
  };
  const [errors, setErrors] = useState({
    name: "",
    contactNumber: "",
  });

  const validateForm = () => {
    const newErrors = {
      name: "",
      contactNumber: "",
    };

    let isValid = true;

    const nameError = validateName(formData.name);

    if (nameError) {
      newErrors.name = nameError;
      isValid = false;
    }

    const phoneRegex = /^[0-9]{10,15}$/;

    if (!phoneRegex.test(formData.contactNumber.trim())) {
      newErrors.contactNumber =
        "Sorry, your input failed. Please enter a valid contact number.";
      isValid = false;
    }

    setErrors(newErrors);

    return isValid;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: `${countryCode}${formData.contactNumber}`,
        message: formData.message,
      };

      const response = await submitContact(payload);

      alert(response.message || "Message sent successfully!");

      setFormData({
        name: "",
        email: "",
        contactNumber: "",
        message: "",
      });

      setErrors({
        name: "",
        contactNumber: "",
      });
    } catch (error) {
      alert("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  const [countries, setCountries] = useState<Country[]>([]);
  const [countryCode, setCountryCode] = useState("+91");

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryList = await getCountries();
        setCountries(countryList);

        const india = countryList.find(
          (country) => country.country_code === "+91",
        );

        if (india) {
          setCountryCode(india.country_code);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCountries();
  }, []);

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="pageWrapper">
      <Header />

      <main className="mainContent">
        {/* LEFT */}
        <div className="leftColumn">
          <h1 className="title">
            Let's talk about <br />
            <span className="highlightText">your brand.</span>
          </h1>

          <div className="infoGrid">
            <div className="infoBlock">
              <div className="iconContainer">
                <MapPin size={20} color="#ffffff" strokeWidth={2} />
              </div>
              <div>
                <h3>Visit Us</h3>
                <a
                  href="https://maps.app.goo.gl/3sYW1KEEprHZMGE58"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ahmedabad, Gujarat, India
                </a>
              </div>
            </div>

            <div className="infoBlock">
              <div className="iconContainer">
                <Mail size={20} color="#ffffff" strokeWidth={2} />
              </div>
              <div>
                <h3>Email</h3>
                <a
                  href="mailto:hello@aigio.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  devashish.saikia@aigio.ai
                </a>
                <br />
                <a
                  href="mailto:support@aigio.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support@aigio.ai
                </a>
              </div>
            </div>

            <div className="infoBlock infoBlock--full">
              <div className="iconContainer">
                <Phone size={20} color="#ffffff" strokeWidth={2} />
              </div>
              <div>
                <h3>Call Us</h3>
                <a
                  href="tel:+919898946987"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +91 98989 46987
                </a>
                <p>Mon–Sat, 11am – 5pm IST</p>
              </div>
            </div>
          </div>

          <div className="mapWrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.0256993704925!2d72.51133267514169!3d23.05951937914785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e9ca97a58f649%3A0x94c0aa5e0eda406e!2sTrinity!5e0!3m2!1sen!2sin!4v1783332861486!5m2!1sen!2sin"
              title="map"
              loading="lazy"
            ></iframe>
          </div>
        </div>

        {/* RIGHT */}
        <div className="rightColumn">
          <form
            className="contactForm"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="formRow">
              <div className="formGroup">
                <label>NAME</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                  autoComplete="off"
                />

                {errors.name && <p className="errorText">{errors.name}</p>}
              </div>

              <div className="formGroup">
                <label>EMAIL</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="formGroup">
              <label htmlFor="contactNumber">CONTACT NUMBER</label>

              <div className="audit-form__phone-group">
                <div
                  className="custom-dropdown"
                  ref={dropdownRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="custom-dropdown__selected"
                    onClick={() => setOpen((prev) => !prev)}
                  >
                    <span>{countryCode || "Select code"}</span>

                    <ChevronDown
                      size={16}
                      className={`custom-dropdown__arrow ${open ? "open" : ""}`}
                    />
                  </div>

                  {open && (
                    <div className="custom-dropdown__menu">
                      <input
                        className="custom-dropdown__search"
                        autoFocus
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search country code..."
                      />

                      <div className="custom-dropdown__list">
                        {countries
                          .filter((c) =>
                            c.country_code
                              .toLowerCase()
                              .includes(search.toLowerCase()),
                          )
                          .map((country) => (
                            <div
                              key={country.id}
                              className="custom-dropdown__item"
                              onClick={() => {
                                setCountryCode(country.country_code);
                                setOpen(false);
                                setSearch("");
                              }}
                            >
                              {country.country_code}
                            </div>
                          ))}

                        {countries.filter((c) =>
                          c.country_code
                            .toLowerCase()
                            .includes(search.toLowerCase()),
                        ).length === 0 && (
                          <div className="custom-dropdown__empty">
                            No results
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

       <input
  type="tel"
  id="contactNumber"
  name="contactNumber"
  value={formData.contactNumber}
  onChange={handleChange}
  placeholder="Enter your contact number"
  required
  autoComplete="off"
  inputMode="numeric"
  maxLength={10}
  pattern="[0-9]{10}"
  title="Enter a valid 10-digit phone number"
  onInput={(e) => {
    e.currentTarget.value = e.currentTarget.value
      .replace(/\D/g, "")
      .slice(0, 10);
  }}
/>
              </div>

              {errors.contactNumber && (
                <p className="errorText">{errors.contactNumber}</p>
              )}
            </div>

            <div className="formGroup">
              <label>MESSAGE</label>
              <textarea
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your brand goals..."
                autoComplete="off"
              />
            </div>

            <button className="submitButton" type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};
